import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import uuid from "uuid";
import styled from "styled-components";
import {
  changeAssetType,
  initialize,
  saveImageToDisk,
  saveToCamio,
  renderMedia
} from "../../actions/publish";
import TimecodeDisplay from "../../components/Editor/TimecodeDisplay";
import { actions as snackbarActions } from "../../actions/snackbar";
import { makeGetConfigValue } from "../../selectors/config";
import ASSET_TYPE from "../../constants/publishingAssetTypes";
import Timecode from "../../util/timecode";
import DraggablePopup from "../DraggablePopup";
import Tabs, { RENDER_MEDIA_TABS } from "./Tabs";
import SaveToDiskTabContent from "./SaveToDiskTabContent";
import LuciTabContent from "./LuciTabContent";
import { ErrorMessage } from "./styled-components";
import { Input } from "../../styled-components/";
import SnackbarButtonTemplates from "../../constants/snackbarButtonTemplates";
import { Container, StickyFooter } from "../../styled-components/Layout";
import localize from "../../util/localizeUtils";

const FieldLabel = styled.div`
  display: inline-block;
  width: 150px;
`;

const FlexInput = styled(Input)`
  flex: 1;
`;

const StoryNameField = styled.div`
  display: flex;
  flex: none;
  flexdirection: row;
  fontsize: 11px;
  padding: 0.75rem;
`;

const RenderAsField = styled.div`
  display: flex;
  flex: none;
  flexdirection: row;
  height: 20px;
  padding: 0 0.75rem;
`;

const AssetTypeRadioContainer = styled.div`
  alignitems: center;
  display: flex;
  flex: 1;
`;

function TextField({ disabled, placeholder, value, onChange }) {
  return (
    <FlexInput
      type="text"
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  );
}

TextField.propTypes = {
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func
};

TextField.defaultProps = {
  disabled: false,
  placeholder: "",
  value: "",
  onChange: () => {}
};

function RadioButton({ checked, disabled, text, onChange }) {
  return (
    <span style={{ alignItems: "center", display: "flex" }}>
      <input
        type="radio"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        value={text.toLowerCase()}
      />
      <span style={{ paddingLeft: "4px", paddingRight: "16px" }}>{text}</span>
    </span>
  );
}

RadioButton.propTypes = {
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

class RenderMediaDialog extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: RENDER_MEDIA_TABS.saveToDisk,
      completedTabs: [],
      duration: 5,
      errorMessage: "",
      fbAuthResponse: {},
      facebookUser: null,
      timecodeDuration: "00:00:05:00",
      filename: "",
      twitterUser: null
    };
  }

  UNSAFE_componentWillMount() {
    if (!this.props.mediaEngineEnabled) {
      this.props.onChangeAssetType(ASSET_TYPE.image.toLowerCase());
    } else if (this.props.clipperMovie) {
      this.props.onChangeAssetType(ASSET_TYPE.movie.toLowerCase());
      this.setState({ timecodeDuration: this.props.clipperDuration });
    }
  }

  componentDidMount() {
    this.props.initialize(this.props.assetType);
  }

  onChangeAssetType = event => {
    this.props.onChangeAssetType(event.target.value);
  };

  onChangeFilename = event => {
    this.setState({ errorMessage: "", filename: event.target.value });
  };

  onChangeTimeCode = newTimeCode => {
    const duration = Timecode.convertToSeconds(
      newTimeCode,
      this.props.frameRate
    );
    this.setState({ duration, timecodeDuration: newTimeCode });
  };

  onSelectTab = tab => {
    this.setState({ activeTab: tab, errorMessage: "" });
  };

  onDownloadToDisk = () => {
    if (!this.state.filename) {
      this.setState({ errorMessage: localize("requiredFilenameError") });
    } else {
      this.props.onSaveImageToDisk(this.state.filename);
    }
  };

  onSaveToDisk = () => {
    if (!this.state.filename) {
      this.setState({ errorMessage: localize("requiredFilenameError") });
    } else if (!this.props.destination) {
      this.setState({ errorMessage: localize("requiredPresetError") });
    } else {
      this.props.onRenderMedia(
        this.state.filename,
        this.state.duration,
        this.props.destination
      );
      this.setState({
        completedTabs: [
          ...this.state.completedTabs,
          RENDER_MEDIA_TABS.saveToDisk
        ]
      });
    }
  };

  onSaveToLuci = () => {
    if (!this.state.filename) {
      this.setState({ errorMessage: localize("requiredFilenameError") });
    } else {
      this.props.onSaveToCamio(this.state.filename, this.state.duration);
      this.setState({
        completedTabs: [...this.state.completedTabs, RENDER_MEDIA_TABS.luci]
      });
      this.props.showSnack(uuid.v4(), {
        content: localize("savedToLuciText"),
        timeout: 7000,
        buttonTemplate: SnackbarButtonTemplates.DISMISS
      });
    }
  };

  renderDurationField() {
    if (this.props.assetType === ASSET_TYPE.image.toLowerCase()) {
      return null;
    }

    return (
      <div style={{ alignItems: "center", display: "flex" }}>
        <TimecodeDisplay
          disabled={!!this.props.clipperMovie}
          label={localize("durationLabel")}
          timecode={this.state.timecodeDuration}
          timecodeChanged={this.onChangeTimeCode}
          frameRate={this.props.frameRate}
          maxSeconds={60}
        />
      </div>
    );
  }

  renderAssetTypeRadio = () => (
    <AssetTypeRadioContainer>
      <RadioButton
        checked={this.props.assetType === ASSET_TYPE.image.toLowerCase()}
        disabled={!!this.props.clipperMovie}
        text={ASSET_TYPE.image}
        onChange={this.onChangeAssetType}
      />
      <RadioButton
        checked={this.props.assetType === ASSET_TYPE.movie.toLowerCase()}
        disabled={!!this.props.clipperMovie || !this.props.mediaEngineEnabled}
        text={ASSET_TYPE.movie}
        onChange={this.onChangeAssetType}
      />
    </AssetTypeRadioContainer>
  );

  renderTabContent() {
    switch (this.state.activeTab) {
      case RENDER_MEDIA_TABS.luci:
        return <LuciTabContent onSave={this.onSaveToLuci} />;
      case RENDER_MEDIA_TABS.saveToDisk:
        return (
          <SaveToDiskTabContent
            onDownload={this.onDownloadToDisk}
            onSave={this.onSaveToDisk}
          />
        );
      default:
        return null;
    }
  }

  render() {
    return (
      <DraggablePopup
        visible={this.props.isOpen}
        title={localize("renderMediaTitle")}
        handle=".handle"
        height={324}
        width={600}
        closeDialog={this.props.closeDialog}
      >
        <Container>
          <Container>
            <StoryNameField>
              <FieldLabel>{localize("filenameLabel")}</FieldLabel>
              <TextField
                placeholder={localize("filenamePlaceholder")}
                value={this.state.filename}
                onChange={this.onChangeFilename}
              />
            </StoryNameField>
            <RenderAsField>
              <FieldLabel>{localize("renderAsLabel")}</FieldLabel>
              {this.renderAssetTypeRadio()}
              {this.renderDurationField()}
            </RenderAsField>
            <Tabs
              activeTab={this.state.activeTab}
              completedTabs={this.state.completedTabs}
              onSelectTab={this.onSelectTab}
            />
            {this.renderTabContent()}
          </Container>
          <StickyFooter>
            <ErrorMessage visible={!!this.state.errorMessage}>
              {this.state.errorMessage}
            </ErrorMessage>
          </StickyFooter>
        </Container>
      </DraggablePopup>
    );
  }
}

RenderMediaDialog.propTypes = {
  assetType: PropTypes.string.isRequired,
  clipperDuration: PropTypes.string,
  clipperMovie: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  closeDialog: PropTypes.func.isRequired,
  destination: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    preset: PropTypes.string.isRequired
  }),
  mediaEngineEnabled: PropTypes.bool,
  initialize: PropTypes.func.isRequired,
  onChangeAssetType: PropTypes.func.isRequired,
  onSaveImageToDisk: PropTypes.func.isRequired,
  onSaveToCamio: PropTypes.func.isRequired,
  frameRate: PropTypes.number.isRequired,
  showSnack: PropTypes.func.isRequired,
  onRenderMedia: PropTypes.func.isRequired
};

RenderMediaDialog.defaultProps = {
  clipperDuration: "00:00:00:00",
  clipperMovie: "",
  destination: null,
  mediaEngineEnabled: false
};

const makeMapStateToProps = () => {
  const getConfigValue = makeGetConfigValue();

  const mapStateToProps = state => {
    const destination = state.publish.destinationList.find(
      d => d.id.toString() === state.publish.destinationId
    );
    return {
      frameRate: parseInt(
        getConfigValue(state, "luci", "automationTimecodeFrameRates", "30"),
        10
      ),
      assetType: state.publish.assetType,
      clipperDuration: state.clipperMovie.duration,
      clipperMovie: state.clipperMovie.fileUrl,
      destination,
      mediaEngineEnabled:
        state.app.connections.mediaEngine.connected &&
        state.app.connections.mediaEngine.licensed
    };
  };

  return mapStateToProps;
};

const mapDispatchToProps = dispatch => ({
  initialize: assetType => {
    dispatch(initialize(assetType));
  },
  onChangeAssetType: assetType => {
    dispatch(changeAssetType(assetType));
  },
  onSaveImageToDisk: title => {
    dispatch(saveImageToDisk(title));
  },
  onSaveToCamio: (title, duration) => {
    dispatch(saveToCamio(title, duration));
  },
  showSnack: (id, data) => {
    dispatch(snackbarActions.showSnack(id, data));
  },
  onRenderMedia: (title, duration, destination) => {
    dispatch(renderMedia(title, duration, destination));
  }
});

export default connect(
  makeMapStateToProps,
  mapDispatchToProps
)(RenderMediaDialog);



// WEBPACK FOOTER //
// ./app/components/RenderMediaDialog/index.js